import React, { type ReactNode, useEffect, useMemo } from "react";

import clsx from "clsx";
import { compact, isEqual, sum } from "lodash";
import { useEffectOnce } from "usehooks-ts";

import { usePageConfiguration } from "@features/sales-v3/hooks";
import { useActiveTab } from "@features/sales-v3/hooks/use-active-tab";
import {
  type FiltersState,
  useTabState,
} from "@features/sales-v3/hooks/use-tab-state";
import { useSplitView } from "@features/sales-v3/hooks/use-table-selection";
import { useTabTopPlatforms } from "@features/sales-v3/table/contexts";
import { useDesignElementManager } from "@hooks/use-design-element-manager";
import { usePrevious } from "@hooks/use-previous";

import { TAB_RANKING, TAB_RANKINGS } from "../constants/tab";

import { type TabId } from "./tabs";

interface Props {
  id: TabId;
  children: ReactNode;
  active?: boolean;
  onClick(): void;
}

export const Tab = ({ id, children, active = false, onClick }: Props) => {
  const { designElementManager } = useDesignElementManager();

  const adTabState = useTabState("ad");
  const adsetTabState = useTabState("adset");
  const campaignTabState = useTabState("campaign");
  const breakdownTabState = useTabState("breakdown");
  const tabStates = useMemo(
    () => ({
      breakdown: breakdownTabState,
      campaign: campaignTabState,
      adset: adsetTabState,
      ad: adTabState,
    }),
    [breakdownTabState, campaignTabState, adsetTabState, adTabState],
  );

  const isSplitView = useSplitView();

  const { activeTab } = useActiveTab();
  const { tabState, setTabState } = useTabState(id);

  const { breakdowns } = usePageConfiguration();
  const prevBreakdown = usePrevious(breakdowns);
  const [topPlatforms, setTopPlatforms] = useTabTopPlatforms(id);

  // on mount, remove the total count since it may be old
  useEffectOnce(() => {
    setTabState((tabState) => ({
      ...tabState,
      preselectionDone: false,
      totalCount: 0,
    }));
  });

  useEffect(() => {
    if (
      prevBreakdown !== undefined &&
      breakdowns &&
      !isEqual(prevBreakdown, breakdowns)
    ) {
      // if any of the tabState atomicities have changed, then reset everything
      setTabState((tabState) => ({
        ...tabState,

        inclusionAdKeys: [],
        inclusionAtomicities: [],
        exclusionAdKeys: [],
        exclusionAtomicities: [],
      }));
    }
  }, [breakdowns, prevBreakdown, setTabState]);

  const platformsToUse = useMemo(() => {
    if (TAB_RANKINGS[activeTab] < TAB_RANKINGS[id]) {
      return [];
    }
    return topPlatforms;
  }, [activeTab, id, topPlatforms]);

  const tabSelectedCount = useMemo(() => {
    // if the active tab is higher ranked, then nothing is selected
    if (TAB_RANKINGS[activeTab] < TAB_RANKINGS[id]) {
      return 0;
    }

    ////////// split view mode //////////////
    if (isSplitView) {
      return getSplitViewCount({
        isBreakdownTab: id === "breakdown",
        filtersState: tabState.proposedFiltersState,
      });
    }

    ////////// aggregate view mode //////////

    // default select ALL mode

    if (tabState.selectAll) {
      return getSelectAllCount({
        totalCount: tabState.totalCount,
        filtersState: tabState.proposedFiltersState,
      });
    }

    // default select NONE mode

    const selectedCount = getSelectNoneMode({
      filtersState: tabState.proposedFiltersState,
    });
    return selectedCount;
  }, [
    activeTab,
    id,
    isSplitView,
    tabState.selectAll,
    tabState.proposedFiltersState,
    tabState.totalCount,
  ]);

  const showCrossIcon = useMemo(() => {
    if (
      !isSplitView &&
      !tabState.proposedFiltersState.inclusionAdKeys.length &&
      !tabState.proposedFiltersState.inclusionAtomicities.length &&
      !tabState.proposedFiltersState.exclusionAdKeys.length &&
      !tabState.proposedFiltersState.exclusionAtomicities.length
    ) {
      return false;
    }
    return true;
  }, [
    isSplitView,
    tabState.proposedFiltersState.exclusionAdKeys.length,
    tabState.proposedFiltersState.exclusionAtomicities.length,
    tabState.proposedFiltersState.inclusionAdKeys.length,
    tabState.proposedFiltersState.inclusionAtomicities.length,
  ]);

  return (
    <div
      onClick={onClick}
      className={clsx(
        "w-64 h-10 px-2.5 flex items-center cursor-pointer z-[2]",
        "border rounded-t-lg text-interstellar+2",
        {
          "bg-[#f8f8f9] border-gray-0 ": !active,
          "hover:border-t-primary hover:border-x-primary hover:bg-cosmic-4":
            !active,
          "bg-white border-t-gray-0 border-x-gray-0 border-b-white": active,
        },
      )}
    >
      <div
        className={clsx("flex-1", {
          "text-primary font-semibold": active,
        })}
      >
        {children}
      </div>
      {compact(platformsToUse).map((platform) => {
        const designElement = designElementManager.getDesignElement(platform);
        if (designElement.isPreset) {
          return null;
        }

        const icon = designElement.getComponent({
          className: "mr-[-6px] z-3",
          borderColor: "#C1C5CD",
        });
        return <span key={platform}>{icon}</span>;
      })}
      {tabSelectedCount > 0 && (
        <div
          onClick={(e) => {
            setTabState((tabState) => ({
              ...tabState,
              proposedFiltersState: {
                inclusionAdKeys: [],
                inclusionAtomicities: [],
                exclusionAdKeys: [],
                exclusionAtomicities: [],
              },
            }));

            const tabsToUnset = TAB_RANKING.slice(
              TAB_RANKING.indexOf(id) + 1,
              TAB_RANKING.length,
            );
            tabsToUnset.forEach((tab) => tabStates[tab].unsetTabState());
            setTopPlatforms([]);
            e.stopPropagation();
          }}
          className={clsx(
            "text-xxs font-normal pl-3 pr-2 py-[1px] flex items-center",
            "border border-primary bg-cosmic-5 rounded-2xl cursor-auto truncate",
          )}
        >
          {tabSelectedCount} selected
          {showCrossIcon && (
            <i
              className={clsx(
                "flex items-center justify-center ml-1 fa-regular fa-sharp",
                "fa-xmark cursor-pointer text-[#021330] text-[1.4em] rounded-full bg-cosmic-5",
                "px-[0.25rem] leading-[1rem]	hover:bg-cosmic-2",
              )}
            />
          )}
        </div>
      )}
    </div>
  );
};

export function getSplitViewCount({
  isBreakdownTab,
  filtersState,
}: {
  isBreakdownTab: boolean;
  filtersState: FiltersState;
}) {
  if (isBreakdownTab) {
    // there are no ancestors for the breakdown tab
    const selectedCount = filtersState.inclusionAtomicities.length;
    return selectedCount;
  }
  const selectedCount = filtersState.inclusionAdKeys.length;

  return selectedCount;
}

export function getSelectAllCount({
  totalCount,
  filtersState,
}: {
  totalCount: number;
  filtersState: FiltersState;
}) {
  const selectedCount =
    totalCount -
    (filtersState.exclusionAdKeys.length +
      sum(filtersState.exclusionAtomicities.map((a) => a.count))) +
    (filtersState.inclusionAdKeys.length +
      sum(filtersState.inclusionAtomicities.map((a) => a.count)));

  return selectedCount;
}

export function getSelectNoneMode({
  filtersState,
}: {
  filtersState: FiltersState;
}) {
  const selectedCount =
    filtersState.inclusionAdKeys.length +
    sum(filtersState.inclusionAtomicities.map((a) => a.count)) +
    -(
      filtersState.exclusionAdKeys.length +
      sum(filtersState.exclusionAtomicities.map((a) => a.count))
    );

  return selectedCount;
}
